import type { IAugmentedJQuery, IModule, IRootScopeService } from "angular";
import { element as angularElement, module } from "angular";
import "angular-ui-bootstrap";

import "angular-agility";
import "angular-debounce";
import "angular-hammer";
import "angular-resize";
import "ui-select";

import "angular-hotkeys";

import type { TransitionService } from "@uirouter/angularjs";
import validationConfig from "../Services/Validation/ValidationConfig";

let aaNotify: any;
let $translate: angular.translate.ITranslateService;

export const app: IModule = module("mmGui", [
    "pascalprecht.translate",
    "ui.bootstrap",
    "aa.formExtensions",
    "aa.formExternalConfiguration",
    "aa.notify",
    "ui.select",
    "hmTouchEvents",
    "cfp.hotkeys",
    // "ngCookies",
    // "angular-jwt",
    // "angularHttpAuthInterceptor",
    // "WebStorageModule",
    "mmServices",
    "mmHttpInterceptors",
    // "mmHttpAuthInterceptors",
    "rt.debounce",
    "rt.resize",
]);

app.config([
    "hotkeysProvider",
    (hotkeysProvider: angular.hotkeys.HotkeysProvider) => {
        (hotkeysProvider as any).useNgRoute = false;
    },
])
    .config([
        "aaNotifyConfigProvider",
        "$httpProvider",
        (aaNotifyConfigProvider: aa.INotifyConfigProvider) => {
            (aaNotifyConfigProvider as any).addOrUpdateNotifyConfig("aaFormExtensionsValidationErrors", {
                options: {
                    showField: (error: any) => {
                        error.field.$element[0].focus();
                    },
                    ttl: 0, // forever until manually removed by form extensions
                },
                template:
                    "<div class='alert alert-danger aa-form-extensions-validation-errors'>" +
                    "<div class='pull-right aa-notify-close' ng-click='close(notification)'>" +
                    "<span class='fa-stack fa-lg'>" +
                    "<i class='fal fa-circle fa-stack-2x'></i>" +
                    "<i class='fal fa-times fa-stack-1x fa-inverse'></i>" +
                    "</span>" +
                    "</div>" +
                    "<strong>{{notification.getValidationTitle() | translate}}: </strong>" +
                    "<ul>" +
                    '<li ng-repeat="error in notification.validationErrorsToDisplay()" style="white-space: nowrap">' +
                    "{{ error.message | irInterpolate }}&nbsp;" +
                    '<a href="" title="Focus Field" ng-show="error.field" ' +
                    ' ng-click="notification.showField(error)"><i class="fal fa-search"></i></a>' +
                    "</li>" +
                    "</ul>" +
                    "</div>",
            });
            // Uncomment below to remove Angular Agility Ajax Interceptor
            //        const index = $httpProvider.interceptors.indexOf("aaAjaxInterceptor");
            //        $httpProvider.interceptors.splice(index, 1);
        },
    ])
    .config([
        "aaFormExtensionsProvider",
        (aaFormExtensionsProvider: aa.IFormExtensionsProvider) => {
            (aaFormExtensionsProvider as any).defaultLblCol = "none";
            (aaFormExtensionsProvider as any).defaultCol = "none";
            (aaFormExtensionsProvider as any).defaultNotifyTarget = "false";

            aaFormExtensionsProvider.labelStrategies.mmLabelStrategy = (
                element: any,
                labelText: string,
                isRequired: boolean,
            ) => {
                const $element = element as IAugmentedJQuery;

                const className = findClosestEleWithAttr($element, "aa-lbl-class") || "";

                const label = angularElement("<label>")
                    .attr("for", $element[0].id)
                    .addClass(` control-label ${className}`)
                    .addClass(isRequired ? "label-required" : "")
                    .html(labelText);

                const unsupported = ["button", "submit"];

                if (unsupported.includes($element[0].nodeName.toLowerCase())) {
                    throw new Error(`Generating a label for and input type ${$element[0].nodeName} is unsupported.`);
                }

                $element.parent().parent().prepend(label);
            };
            aaFormExtensionsProvider.defaultLabelStrategy = "mmLabelStrategy";

            aaFormExtensionsProvider.onNavigateAwayStrategies.mmNavigateAwayStrategy = (
                rootFormScope: ng.IScope,
                rootForm: any,
                $injector: ng.auto.IInjectorService,
            ) => {
                // const $rootForm = rootForm as IAugmentedJQuery;

                const confirmationMessage =
                    "Du har ändringar som inte har sparats. Är du säker på att du vill lämna sidan?";

                const $transitions: TransitionService = $injector.get("$transitions");
                const removeStateChangeHook = $transitions.onBefore({}, () => {
                    if (rootForm.$aaFormExtensions.$changed) {
                        // Don't botstrapp annoying message /Victor
                        // if (!window.confirm(confirmationMessage)) {
                        //     return false;
                        // }
                    }
                    return true;
                });

                // NATIVE DOM IE9+
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                function beforeUnload(e: any) {
                    if (rootForm.$aaFormExtensions.$changed) {
                        (e || window.event).returnValue = confirmationMessage;
                        return confirmationMessage;
                    }
                    return undefined;
                }

                // angularElement(window).on("beforeunload", beforeUnload);

                rootFormScope.$on("$destroy", () => {
                    removeStateChangeHook();
                });
            };
            aaFormExtensionsProvider.defaultOnNavigateAwayStrategy = "mmNavigateAwayStrategy";

            // recurse up document tree starting with the current element to try to find
            // and element with a given attribute. if found return it.
            function findClosestEleWithAttr(ele: any, attr: any): any {
                let attrVal: any;

                attrVal = ele.attr(attr);
                if (attrVal) {
                    return attrVal;
                }

                const parent = ele.parent();

                if (!parent.length) {
                    return null;
                }

                attrVal = parent.attr(attr);
                if (attrVal) {
                    return attrVal;
                }

                return findClosestEleWithAttr(parent, attr);
            }
        },
    ])
    .config(validationConfig)
    .run([
        "aaNotify",
        "$translate",
        "$rootScope",
        (
            aaNotifyInstance: any,
            $translateInstance: angular.translate.ITranslateService,
            $rootScope: IRootScopeService,
        ) => {
            /* eslint-disable @typescript-eslint/no-unused-vars */
            aaNotify = aaNotifyInstance;
            $translate = $translateInstance;
            /* eslint-enable @typescript-eslint/no-unused-vars */

            const er = /^[A-Za-z0-9_+-]+(\.[A-Za-z0-9_+-]+)*@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.([a-z]{2,10})$/;
            const unc =
                /^((?:[a-zA-Z]:[\\|\/])|(?:[\\]{2}((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))|(?:[\\]{2}(?:[a-zA-Z _$.-]+)))(?:[.\/\\ ](?![.\/\\\n])|[^<>:"|?*.\/\\ \n])+$/;

            ($rootScope as any).validation = {
                emailPattern: er,
                pathPattern:
                    /^((?:[a-zA-Z]:)|(?:[\\]{2}(?:[0-9]{1,3}\.){3}(?:[0-9]{1,3}){1}))([\\]{1}[^<>:""|?*./\\\n]*)*$/,
                // +46 (0)771-41 11 00     is ok by below pattern /Victor
                phoneNumberPattern: /^\+(?:[(]?([0-9])[)]?[- ]?){6,14}[0-9]$/,
                uncPattern: unc,
            };
        },
    ]);
